import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, ExternalLink, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/PageHeader';
import { DesktopOnly } from '@/components/dashboard/DesktopOnly';
import { SearchInput } from '@/components/ui/search-input';
import { api } from '@/api/client';
import { useCippTenant, type CippTenantDetail, type CippTenantUser } from '@/api/cipp';
import { TenantSecurityPanel } from '@/components/cipp/TenantSecurityPanel';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { cippDevicesView, insightsPersonPath, ROUTES } from '@/lib/routes';
import { cn } from '@/lib/utils';
import { OrbitLink } from '@/components/insights/OrbitLink';

/**
 * CIPP → one tenant's users. Built only from CIPP's cached MFA + mailbox reports
 * (see server/cipp/routes.ts), so it loads in about a second. Each user links into
 * CIPP for anything deeper — Cosmos is read-only here.
 */

type Filter = 'all' | 'nomfa' | 'admins' | 'forwarding' | 'disabled' | 'guests';

const isMember = (u: CippTenantUser) => u.userType !== 'Guest';
/** Enabled, licensed members with no MFA registered — the people who actually matter for MFA gaps. */
const noMfa = (u: CippTenantUser) => u.enabled && u.licensed && isMember(u) && u.mfaRegistered === false;

const FILTERS: { id: Filter; label: string; test: (u: CippTenantUser) => boolean; tone?: 'bad' | 'warn' }[] = [
  { id: 'all',        label: 'All users',   test: () => true },
  { id: 'nomfa',      label: 'No MFA',      test: noMfa, tone: 'bad' },
  { id: 'admins',     label: 'Admins',      test: (u) => u.isAdmin, tone: 'warn' },
  { id: 'forwarding', label: 'Forwarding',  test: (u) => !!u.mailbox?.forwardingTo, tone: 'bad' },
  { id: 'disabled',   label: 'Sign-in blocked', test: (u) => !u.enabled },
  { id: 'guests',     label: 'Guests',      test: (u) => !isMember(u) },
];

const MFA_METHOD_LABELS: Record<string, string> = {
  microsoftAuthenticatorPush: 'Authenticator', softwareOneTimePasscode: 'Authenticator code',
  windowsHelloForBusiness: 'Windows Hello', mobilePhone: 'Phone', alternateMobilePhone: 'Alt phone',
  officePhone: 'Office phone', fido2: 'Security key', passKeyDeviceBound: 'Passkey',
  passKeyDeviceBoundAuthenticator: 'Passkey', email: 'Email', temporaryAccessPass: 'TAP',
  microsoftAuthenticatorPasswordless: 'Passwordless',
};

export default function CippTenantDetailPage() {
  const { customerId } = useParams<{ customerId: string }>();
  const tenant = useCippTenant(customerId);
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const users = tenant.data?.users ?? [];
  const counts = useMemo(
    () => Object.fromEntries(FILTERS.map((f) => [f.id, users.filter(f.test).length])) as Record<Filter, number>,
    [users],
  );
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const test = FILTERS.find((f) => f.id === filter)!.test;
    return users.filter(test).filter((u) => !q || u.displayName.toLowerCase().includes(q) || u.upn.toLowerCase().includes(q));
  }, [users, filter, search]);

  async function refresh() {
    setRefreshing(true);
    try {
      const fresh = await api.get<CippTenantDetail>(`/api/cipp/tenants/${customerId}?fresh=1`);
      qc.setQueryData(['cipp', 'tenant', customerId], fresh);
    } finally {
      setRefreshing(false);
    }
  }

  const t = tenant.data?.tenant;

  return (
    <DesktopOnly title="CIPP is available on desktop" message="Open Cosmos on a larger screen to browse Microsoft 365 tenants.">
      <div className="mx-auto w-full max-w-[1400px] px-4 py-4">
        <Link to={ROUTES.cipp.tenants} className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3 w-3" /> Tenants
        </Link>

        <PageHeader
          title={t?.displayName ?? 'Tenant'}
          count={tenant.data ? visible.length : null}
          isFetching={tenant.isFetching || refreshing}
          subtitle={t && <>
            {t.defaultDomainName}
            {' · '}
            {t.mapping && !t.mapping.ignored ? <OrbitLink companyId={t.mapping.companyId}>{t.mapping.companyName}</OrbitLink> : <span className="italic">not mapped to an Autotask company</span>}
            {!t.healthy && <span className="ml-2 inline-flex items-center gap-1 text-destructive"><AlertTriangle className="h-3 w-3" /> CIPP can't reach this tenant</span>}
          </>}
          actions={tenant.data && <>
            <Button variant="ghost" size="sm" onClick={refresh} disabled={refreshing} title="Re-read CIPP's reports now">
              <RefreshCw className={cn('h-3.5 w-3.5', refreshing && 'animate-spin')} />
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to={cippDevicesView({ tenant: tenant.data.tenant.customerId })}>Intune devices</Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <a href={tenant.data.cippUrl} target="_blank" rel="noreferrer">
                <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Open in CIPP
              </a>
            </Button>
          </>}
        />

        {tenant.error && (
          <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            Failed to load the tenant: {tenant.error instanceof Error ? tenant.error.message : 'Unknown error'}
          </div>
        )}
        {tenant.isLoading && <div className="py-10 text-center text-sm text-muted-foreground">Loading users from CIPP…</div>}

        {tenant.data && (
          <>
            {(tenant.data.errors.mfa || tenant.data.errors.mailboxes) && (
              <div className="mt-3 rounded-md border border-warning/30 bg-warning-soft px-3 py-2 text-xs text-warning">
                {tenant.data.errors.mfa && <div>MFA report unavailable: {tenant.data.errors.mfa}</div>}
                {tenant.data.errors.mailboxes && <div>Mailbox report unavailable: {tenant.data.errors.mailboxes}</div>}
              </div>
            )}

            <TenantSecurityPanel customerId={tenant.data.tenant.customerId} />

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <SearchInput withIcon placeholder="Name or email…" className="w-64" value={search} onValueChange={setSearch} />
              <div className="flex flex-wrap gap-1 rounded-lg bg-muted p-1">
                {FILTERS.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setFilter(f.id)}
                    className={cn(
                      'rounded-md px-3 py-1 text-xs font-medium transition-colors',
                      filter === f.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {f.label}{' '}
                    <span className={cn(
                      'ml-1 tabular-nums',
                      counts[f.id] > 0 && f.tone === 'bad' ? 'text-destructive' : counts[f.id] > 0 && f.tone === 'warn' ? 'text-warning' : 'text-muted-foreground',
                    )}>
                      {counts[f.id]}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-3 overflow-hidden rounded-lg border border-border bg-card shadow-sm">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr className="text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-2.5">User</th>
                    <th className="min-w-[220px] px-4 py-2.5">Account</th>
                    <th className="min-w-[190px] px-4 py-2.5">MFA</th>
                    <th className="px-4 py-2.5">Mailbox</th>
                    <th className="w-[1%] px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-divider">
                  {visible.map((u) => <UserRow key={u.upn} u={u} />)}
                  {visible.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-muted-foreground">No users match.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <p className="mt-2 text-[11px] text-muted-foreground">
              From CIPP's reports — MFA {tenant.data.asOf.mfa ? fmtRelative(tenant.data.asOf.mfa) : 'n/a'},
              {' '}mailboxes {tenant.data.asOf.mailboxes ? fmtRelative(tenant.data.asOf.mailboxes) : 'n/a'}.
              {' '}CIPP refreshes these on its own schedule.
            </p>
          </>
        )}
      </div>
    </DesktopOnly>
  );
}

function Pill({ tone, children, title }: { tone: 'good' | 'warn' | 'bad' | 'muted'; children: React.ReactNode; title?: string }) {
  const cls = {
    good: 'bg-success-soft text-success',
    warn: 'bg-warning-soft text-warning',
    bad: 'bg-destructive/10 text-destructive',
    muted: 'bg-muted text-muted-foreground',
  }[tone];
  return <span title={title} className={cn('inline-flex items-center rounded-full px-1.5 py-0.5 text-[10.5px] font-semibold', cls)}>{children}</span>;
}

function UserRow({ u }: { u: CippTenantUser }) {
  // Defensive: the server normalises this, but a string here once blanked the whole page.
  const methods = [...new Set((Array.isArray(u.mfaMethods) ? u.mfaMethods : []).map((m) => MFA_METHOD_LABELS[m] ?? m))];
  const mb = u.mailbox;
  const usage = mb?.sizeGb != null && mb.quotaGb ? mb.sizeGb / mb.quotaGb : null;
  // MFA only matters for people who can actually sign in.
  const mfaRelevant = u.enabled && isMember(u);

  return (
    <tr className={cn('align-top hover:bg-muted/30', !u.enabled && 'opacity-70')}>
      <td className="max-w-[380px] px-4 py-2.5">
        <Link to={insightsPersonPath(u.upn.toLowerCase())} className="font-medium hover:underline" title="Person Orbit">{u.displayName}</Link>
        <div className="truncate text-xs text-muted-foreground" title={u.upn}>{u.upn}</div>
      </td>
      <td className="px-4 py-2.5">
        <div className="flex flex-wrap gap-1">
          {u.enabled ? <Pill tone="good">Enabled</Pill> : <Pill tone="muted">Sign-in blocked</Pill>}
          {u.isAdmin && <Pill tone="warn">Admin</Pill>}
          {!isMember(u) && <Pill tone="muted">Guest</Pill>}
          {!u.licensed && isMember(u) && <Pill tone="muted">Unlicensed</Pill>}
        </div>
      </td>
      <td className="px-4 py-2.5">
        {!mfaRelevant ? <span className="text-xs text-muted-foreground">—</span> : (
          <div className="space-y-0.5">
            {u.mfaRegistered === true ? <Pill tone="good">Registered</Pill>
              : u.mfaRegistered === false ? <Pill tone={u.licensed ? 'bad' : 'warn'}>Not registered</Pill>
              : <Pill tone="muted">Unknown</Pill>}
            {methods.length > 0 && <div className="text-xs text-muted-foreground">{methods.join(', ')}</div>}
            <div className={cn('text-[11px]', u.mfaEnforcedBy ? 'text-muted-foreground' : 'text-warning')}>
              {u.mfaEnforcedBy ?? 'Not enforced by policy'}
            </div>
          </div>
        )}
      </td>
      <td className="px-4 py-2.5 text-xs">
        {!mb ? <span className="text-muted-foreground">—</span> : (
          <div className="space-y-0.5">
            <div>
              {mb.type === 'UserMailbox' ? 'User' : mb.type === 'SharedMailbox' ? 'Shared' : mb.type ?? '—'}
              {mb.sizeGb != null && (
                <span className={cn('ml-1.5', usage != null && usage >= 0.9 ? 'text-destructive' : usage != null && usage >= 0.75 ? 'text-warning' : 'text-muted-foreground')}>
                  {mb.sizeGb.toFixed(1)}{mb.quotaGb ? ` / ${Math.round(mb.quotaGb)} GB` : ' GB'}
                </span>
              )}
            </div>
            {mb.forwardingTo && (
              <div className="inline-flex items-center gap-1 text-destructive">
                <AlertTriangle className="h-3 w-3" /> Forwards to {mb.forwardingTo}
              </div>
            )}
            {mb.litigationHold && <div className="text-muted-foreground">Litigation hold</div>}
          </div>
        )}
      </td>
      <td className="px-4 py-2.5">
        {u.cippUrl && (
          <a href={u.cippUrl} target="_blank" rel="noreferrer" className="inline-flex rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" title="Open in CIPP" aria-label={`Open ${u.displayName} in CIPP`}>
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </td>
    </tr>
  );
}
